import { useEffect, useRef, useState } from "react";
import { getAppUrl } from "../../config";

const serviceLinks = [
	{
		href: getAppUrl("accountApp"),
		title: "b+ account",
		description: "アカウント管理",
	},
	{
		href: getAppUrl("ticketApp"),
		title: "b+ ticket",
		description: "チケット販売",
	},
] as const;

export default function HeroSection() {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [parallaxOffset, setParallaxOffset] = useState(0);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	useEffect(() => {
		let animationFrameId = 0;

		const handleScroll = () => {
			if (animationFrameId) {
				return;
			}

			animationFrameId = window.requestAnimationFrame(() => {
				setParallaxOffset(window.scrollY * 0.35);
				animationFrameId = 0;
			});
		};

		handleScroll();
		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => {
			window.removeEventListener("scroll", handleScroll);
			if (animationFrameId) {
				window.cancelAnimationFrame(animationFrameId);
			}
		};
	}, []);

	return (
		<section className="relative h-screen bg-black text-white flex items-end overflow-hidden">
			{/* Background Image */}
			<div className="absolute inset-0">
				<img
					className="h-[120%] w-full object-cover opacity-40"
					src="/images/welcome/welcome-bg.jpg"
					alt="background"
					style={{
						transform: `translateY(-${parallaxOffset}px)`,
					}}
				/>
				<div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black" />
			</div>

			{/* Content */}
			<div className="relative z-10 container mx-auto px-6 pb-32">
				<div className="flex flex-col md:flex-row md:items-end md:justify-between gap-12">
					{/* Left: Slogan */}
					<div>
						<p className="text-xs tracking-[0.3em] uppercase text-neutral-400 mb-8">
							Established in 2024
						</p>

						<h1 className="text-5xl md:text-7xl 2xl:text-8xl font-light leading-[1.05] tracking-tight mb-6">
							Reinvent everything
							<br />
							about music.
						</h1>

						<p className="text-lg md:text-xl text-neutral-400 max-w-xl leading-relaxed">
							音楽にまつわるすべてを再発明する
						</p>
					</div>

					{/* Right: CTA Buttons */}
					<div className="flex flex-col gap-3 md:pb-1 shrink-0">
						<a
							href="#about"
							className="px-7 py-3.5 bg-white text-black text-sm font-medium rounded-full hover:bg-neutral-200 transition-colors duration-300 text-center"
						>
							私たちについて
						</a>

						{/* Dropdown Button */}
						<div className="relative" ref={dropdownRef}>
							<button
								type="button"
								onClick={() =>
									setIsDropdownOpen((prevIsDropdownOpen) => !prevIsDropdownOpen)
								}
								className="w-full px-7 py-3.5 text-sm font-medium text-white rounded-full border border-white/20 hover:border-white/40 transition-colors duration-300 flex items-center justify-center gap-2"
							>
								サービスを見る
								<svg
									className={`w-4 h-4 transition-transform duration-200 ${
										isDropdownOpen ? "rotate-180" : ""
									}`}
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="1.5"
										d="M5 15l7-7 7 7"
									/>
								</svg>
							</button>

							{/* Dropdown Menu (opens upward) */}
							{isDropdownOpen && (
								<div className="absolute bottom-full mb-2 w-full bg-black rounded-xl border border-white/10 overflow-hidden z-50">
									{serviceLinks.map((serviceLink, index) => (
										<a
											key={serviceLink.href}
											href={serviceLink.href}
											className={`block px-5 py-4 hover:bg-white/5 transition-colors text-left ${
												index === 0 ? "border-b border-white/[0.06]" : ""
											}`}
										>
											<div className="text-sm font-medium text-white mb-0.5">
												{serviceLink.title}
											</div>
											<div className="text-xs text-neutral-500">
												{serviceLink.description}
											</div>
										</a>
									))}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Scroll line */}
			<div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3">
				<span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">
					Scroll
				</span>
				<div className="w-px h-10 bg-gradient-to-b from-neutral-500 to-transparent" />
			</div>
		</section>
	);
}
